<script>
import { mapActions, mapState } from 'vuex'
import * as date from '@/lib/date'
import Popper from '@/views/components/UI/Popper.js'

export default {
  name: 'PostCell',
  components: { Popper },
  props: {
    post: { type: Object, required: true },
    active: { type: Boolean, default: false }
  },

  data: () => ({
  }),

  computed: {
    ...mapState(['user']),
    adminOrEditor () {
      return !!this.user && (this.user.role === 'admin' || this.user.role === 'editor')
    },
    routerLink () {
      if (this.post.type === 'post') {
        return { name: 'work', params: { id: this.post.id } }
      } else if (this.post.type === 'event') {
        return { name: 'event', params: { id: this.post.id } }
      } else {
        return { name: this.post.id }
      }
    },
    routerTypeLink () {
      if (!this.post) return null
      if (this.post.partOfProgramme) {
        return { name: 'programme', params: { id: this.post.partOfProgramme.programmeId } }
      }
      return null
    },

    typeLabel () {
      if (!this.post) return null
      if (this.post.partOfProgramme) {
        return this.post.partOfProgramme.title || this.post.partOfProgramme.singlePostLabel
      } else {
        switch (this.post.type) {
          case 'post': return `Blog ${this.post.author.displayName}`
          case 'event': return 'Event'
          default: return this.post.type
        }
      }
    },
    title () {
      if (this.post.partOfProgramme && this.post.countNumber) {
        return `${this.post.partOfProgramme.singlePostLabel} #${this.post.countNumber}`
      }
      return this.post.title || '?'
    },
    // shortTitle () {
    //   return this.post.shortTitle || this.post.title || '?'
    // },
    shortTitleFirstChar () {
      return this.post.shortTitle.charAt(0)
    },
    shortTitleRest () {
      return this.post.shortTitle.slice(1)
    },

    technique () {
      return this.post.technique || ''
    },

    secondTittle () {
      if (this.post.partOfProgramme && this.post.countNumber) {
        return this.post.title
      }
      return ''
    },
    formattedDate () {
      return date.format(
        this.post.date,
        this.post.type === 'event' && this.upcoming ? 'datetime' : '', 'de'
      )
    },
    cellSize () {
      return (this.post || {}).cardSize || ''
    },
    thumbnailUrl () {
      const post = this.post
      let src = null
      if (post.attachments) {
        // eslint-disable-next-line no-prototype-builtins
        if (post.thumbnail && post.attachments.hasOwnProperty(post.thumbnail)) {
          src = post.attachments[post.thumbnail].srcSet
        } else {
          const firstVisualAttachment = Object.values(post.attachments).find(attachment => {
            return (attachment.type || '').startsWith('image/') ||
              // eslint-disable-next-line no-prototype-builtins
              attachment.srcSet.hasOwnProperty('full') ||
              // eslint-disable-next-line no-prototype-builtins
              attachment.srcSet.hasOwnProperty('preview')
          })
          if (firstVisualAttachment) {
            src = firstVisualAttachment.srcSet
          }
        }
      }
      if (!src) return ''
      const { full, preview, original } = src
      return post.cardSize
        ? (full || original || preview || {}).url
        : (preview || full || original || {}).url
    },
    hosts () {
      if (!this.post || !this.post.participants) return []
      return Object.values(this.post.participants || []).filter(p => p.star)
    },

    participants () {
      if (!this.post || !this.post.participants) return []
      return Object.values(this.post.participants || []).filter(p => !p.star)
    },

    eventData () {
      return {
        eventId: this.post.id,
        eventUrl: window.location.origin + this.$router.resolve(this.routerLink).href,
        eventInfo: {
          title: this.title,
          secondTittle: this.secondTittle,
          dateString: this.formattedDate,
          locationString: (this.post.location || {}).address
        }
      }
    }
  },

  methods: {
    ...mapActions(['showEditor']),
    openEditor (post) {
      this.showEditor({
        type: post.type,
        value: post
      })
    }
  }
}
</script>

<template>
  <div class="post-cell cell flex flex-col" :class="[cellSize, post.status, post.type]">
    <header v-if="adminOrEditor" class="flex-grow-0 absolute">
      <div class="flex items-center relative">
        <div v-if="post.status && post.status !== 'public'" class="status">
          {{ post.status }}
        </div>
      </div>
    </header>
    <button v-if="post.id === $route.params.id || active" class="active" @click="$emit('click')">
      <h1 class="title-letter">
        {{ shortTitleFirstChar }}<span>{{ shortTitleRest }}</span>
      </h1>
    </button>
    <popper
      v-else
      trigger="hover"
      placement="top"
      boundaries-selector="body"
      :boundaries-padding="10">
      <template v-slot:reference>
        <router-link :to="routerLink" class="block">
          <h1 class="title-letter">
            {{ shortTitleFirstChar }}<span>{{ shortTitleRest }}</span>
          </h1>
        </router-link>
      </template>
      <router-link :to="routerLink" class="popper-content">
        <div>
          <header v-if="adminOrEditor" class="h-3">
            <button v-if="adminOrEditor"
              class="button edit-button absolute top-0 right-0 compact w-2/3base h-2/3base text-gray-600 hover:text-white ml-auto"
              @click="openEditor(post)">
              <i class="material-icons text-base cursor-pointer">edit</i>
            </button>
          </header>
          <h3 class="uppercase">{{ title }}</h3>
          <div>{{ technique }}{{ this.post.year ? ` | ${this.post.year}` : '' }}</div>
        </div>
      </router-link>
    </popper>
    <slot></slot>
  </div>
</template>

<style lang="scss">
.post-cell {
  --color-action: white;
  --color-action-focus: #4f4fff;
  --color-action-focus: color-mix(in lab, var(--color-action), blue 20%);
  position: relative;

  .title-letter {
    font-size: var(--title-letter-font-size);

    span {
      font-size: 0.5em;
    }
  }

  & button.active,
  & a {
    display: block;
    height: auto;
    padding: 0 var(--small-padding);
  }
  button.active {
    text-decoration: underline;
  }

  &.draft {
    &:after {
      border-style: dashed;
      border-color: var(--color-aba-red);
    }

    & img {
      opacity: 0.5;
    }

    & * {
      font-style: italic !important;
    }

    a {
      @apply text-gray-500;

      &:hover {
        @apply text-aba-red no-underline;
      }
    }

    .status {
      background: var(--color-aba-red);
    }
  }

  a.popper-content {
    width: max-content;
  }

  .type-badge {
    text-transform: capitalize;
  }

  .badge {
    @apply px-sm py-xs text-xs capitalize truncate bg-gray-200;
    border-radius: 1px;
    min-width: 0;
  }

  .upcoming-label {
    position: relative;
    z-index: 10;
    padding: 0.1rem 0.4rem;
    background: var(--color-aba-blue);
    color: #fff;
    font-size: 75%;
    text-transform: uppercase;
    left: -6px;
    top: -4px;
    transform: rotate(3deg);
  }

  .status {
    @apply text-xs;
    position: absolute;
    white-space: nowrap;
    text-transform: capitalize;
    padding: 0.18rem 0.45rem;
    bottom: calc(100% - 7px);
    left: -5px;
    font-size: 87%;
    background: var(--color-aba-blue);
    color: white;
    font-weight: lighter;
    border-radius: 1rem;
    z-index: 100;
    transform: rotate(-1.5deg);
  }

  a.edit-button {
    text-decoration: none !important;
  }

  .excerpt {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;

    &.short {
      -webkit-line-clamp: 2;
    }
  }

  &.horizontal {
    @media (min-width: 472px) {
      grid-column: auto / span 2;

      h1 {
        font-size: var(--h2);
      }
    }
  }

  &.vertical {
    grid-row: span 2;
  }

  &.big {
    grid-row: span 2;

    @media (min-width: 472px) {
      grid-column: span 2;

      h1 {
        font-size: var(--h1);
      }
    }
  }

}
</style>
